import { useState, useEffect, useCallback } from "react";
import BrandLogo from "@/components/BrandLogo";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, ShieldAlert, LogOut, ArrowLeft, Lock, Mail,
  LayoutDashboard, Users as UsersIcon, DollarSign, Megaphone, ArrowRight,
  Radio, Inbox, Crown, Layers,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import AdminTelemetry from "@/components/admin/AdminTelemetry";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminPricing from "@/components/admin/AdminPricing";
import AdminAds from "@/components/admin/AdminAds";
import AdminEvents from "@/components/admin/AdminEvents";
import AdminInbox from "@/components/admin/AdminInbox";
import AdminBroadcast from "@/components/admin/AdminBroadcast";
import AdminRatings from "@/components/admin/AdminRatings";
import AdminSubscriptions from "@/components/admin/AdminSubscriptions";
import AdminTiers from "@/components/admin/AdminTiers";

const ADMIN_ROLES = ["admin", "super_admin"];

export default function Admin() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center cyber-grid" data-testid="admin-loading" style={{ background: "var(--bg)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-primary-c" />
      </div>
    );
  }
  if (user === false) return <AdminLogin />;
  if (!ADMIN_ROLES.includes(user.role)) return <AccessDenied />;
  return <AdminPanel />;
}

function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (!ADMIN_ROLES.includes(u.role)) {
        toast.error("This account has no admin privileges");
      } else {
        toast.success("Welcome, admin");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen cyber-grid grain flex items-center justify-center px-5" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="absolute top-5 left-5"><button data-testid="admin-back-home" onClick={() => nav("/")} className="font-display font-black tracking-tighter"><BrandLogo /></button></div>
      <div className="absolute top-5 right-5"><ThemeToggle /></div>
      <div className="absolute inset-0" style={{ background: "radial-gradient(700px 400px at 50% 20%, var(--glow), transparent 70%)" }} />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-sm rounded-3xl glow-border p-8" style={{ background: "var(--surface-glass)" }} data-testid="admin-login-card">
        <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mb-5 pulse-ring">
          <ShieldCheck className="w-7 h-7 text-primary-c" />
        </div>
        <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Restricted Access</span>
        <h1 className="font-display font-bold tracking-tight text-2xl mt-1">Admin Portal</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Sign in with a privileged account to manage PointControl.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />
            <input data-testid="admin-email" type="email" required placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl bg-surface glow-border pl-11 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />
            <input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl bg-surface glow-border pl-11 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={busy}
            className="btn-glow w-full rounded-xl py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Enter Console <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function AccessDenied() {
  const nav = useNavigate();
  const { logout } = useAuth();
  return (
    <div className="min-h-screen cyber-grid flex flex-col items-center justify-center gap-5 px-5 text-center" data-testid="admin-access-denied" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <ShieldAlert className="w-12 h-12 text-primary-c" />
      <div>
        <h1 className="font-display font-bold text-2xl">Access denied</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Your account doesn't have admin privileges.</p>
      </div>
      <div className="flex gap-3">
        <button data-testid="denied-dashboard" onClick={() => nav("/dashboard")} className="glass rounded-full px-5 py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>Go to Dashboard</button>
        <button data-testid="denied-logout" onClick={async () => { await logout(); }} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><LogOut className="w-4 h-4" /> Switch account</button>
      </div>
    </div>
  );
}

function AdminPanel() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("telemetry");
  const [inboxUnread, setInboxUnread] = useState(0);
  const [subPending, setSubPending] = useState(0);

  const loadUnread = useCallback(() => {
    api.get("/admin/messages/unread-count").then(({ data }) => setInboxUnread(data.unread || 0)).catch(() => {});
    api.get("/admin/subscriptions/pending-count").then(({ data }) => setSubPending(data.pending || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 12000);
    return () => clearInterval(t);
  }, [loadUnread]);

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };

  const tabs = [
    { v: "telemetry", l: "Telemetry", Icon: LayoutDashboard },
    { v: "events", l: "Events", Icon: Radio },
    { v: "users", l: "Users", Icon: UsersIcon },
    { v: "messages", l: "Messages", Icon: Inbox, badge: inboxUnread },
    { v: "subscriptions", l: "Subscriptions", Icon: Crown, badge: subPending },
    { v: "access", l: "Tiers & Access", Icon: Layers },
    { v: "broadcast", l: "Broadcast", Icon: Megaphone },
    { v: "pricing", l: "Pricing", Icon: DollarSign },
    { v: "ads", l: "Ads & Sponsors", Icon: Megaphone },
    { v: "ratings", l: "Ratings", Icon: ShieldAlert },
  ];

  return (
    <div className="min-h-screen grain" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheck className="w-5 h-5 text-primary-c shrink-0" />
            <span className="font-display font-black tracking-tighter shrink-0"><BrandLogo /></span>
            <span className="hidden sm:inline text-xs tracking-[0.25em] uppercase font-bold" style={{ color: "var(--text-2)" }}>/ Admin Console</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs mono glass rounded-full px-3 py-1" style={{ color: "var(--text-2)" }}>{user.email}</span>
            <ThemeToggle />
            <button data-testid="admin-exit" onClick={() => nav("/")} className="w-10 h-10 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform" aria-label="Back to site"><ArrowLeft className="w-4 h-4 text-primary-c" /></button>
            <button data-testid="admin-logout" onClick={doLogout} className="w-10 h-10 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform" aria-label="Logout"><LogOut className="w-4 h-4 text-primary-c" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Command Center</span>
          <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2">Admin Console</h1>
        </motion.div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="glass rounded-full p-1 flex flex-wrap gap-1 h-auto" data-testid="admin-tabs">
            {tabs.map(({ v, l, Icon, badge }) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}
                className="relative rounded-full px-4 py-2 text-sm font-bold data-[state=active]:bg-[var(--primary)] data-[state=active]:text-[var(--bg)] flex items-center gap-2">
                <Icon className="w-4 h-4" /> {l}
                {badge > 0 && (
                  <span data-testid={`admin-tab-${v}-badge`} className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "#ef4444", color: "#fff" }}>{badge}</span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="telemetry" className="mt-6"><AdminTelemetry /></TabsContent>
          <TabsContent value="events" className="mt-6"><AdminEvents /></TabsContent>
          <TabsContent value="users" className="mt-6"><AdminUsers /></TabsContent>
          <TabsContent value="messages" className="mt-6"><AdminInbox onUnreadChange={setInboxUnread} /></TabsContent>
          <TabsContent value="subscriptions" className="mt-6"><AdminSubscriptions onPendingChange={loadUnread} /></TabsContent>
          <TabsContent value="access" className="mt-6"><AdminTiers /></TabsContent>
          <TabsContent value="broadcast" className="mt-6"><AdminBroadcast /></TabsContent>
          <TabsContent value="pricing" className="mt-6"><AdminPricing /></TabsContent>
          <TabsContent value="ads" className="mt-6"><AdminAds /></TabsContent>
          <TabsContent value="ratings" className="mt-6"><AdminRatings /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
